import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <Camera3D>: renders a Godot Camera3D as a non-active R3F camera with Godot's editor gizmo, so
 * its placement shows in the editor viewport. The active viewport camera lives on the host
 * <Canvas>. This is a passive scene-tree node.
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { fitToAspect, fittedAspect, setLens } from './projectionFit';
import { transformFromNode3DProperties } from '../../../r3f/nodeTransform';
import { useNodePath } from '../../../r3f/contexts/NodePathContext';
import { Camera3DProjectionType } from '../../../godot/scene/camera3d';
import { CameraGizmo } from './Gizmo';
import { usePublishCamera } from '../../../r3f/environment/worldExposure';
import { useResolvedCameraAttributes } from '../../../r3f/environment/useResolvedCameraAttributes';
import { useRegisterSceneCamera } from '../../../r3f/sceneCameras';
import { useResolvedEnvironment } from '../worldenvironment/useResolvedEnvironment';
const DEFAULT_ASPECT = 16 / 9;
export function Camera3D({ node, children }) {
    const properties = node.properties;
    const tscnPath = useNodePath() ?? node.name;
    usePublishCamera(tscnPath, properties.current, {
        attributes: useResolvedCameraAttributes(properties.attributes),
        environment: useResolvedEnvironment(properties.environment),
    });
    const { position, rotation, scale } = useMemo(() => transformFromNode3DProperties(properties), [properties]);
    // `h_offset` / `v_offset` shift the camera along its local right / up vectors after the base
    // transform: the unnormalised basis_x / basis_y columns mixed into the world-space position.
    const offsetPosition = useMemo(() => {
        const t = properties.transform;
        if (!t)
            return position;
        const h = properties.h_offset ?? 0;
        const v = properties.v_offset ?? 0;
        if (h === 0 && v === 0)
            return position;
        return [
            position[0] + t.basis_x.x * h + t.basis_y.x * v,
            position[1] + t.basis_x.y * h + t.basis_y.y * v,
            position[2] + t.basis_x.z * h + t.basis_y.z * v,
        ];
    }, [position, properties.transform, properties.h_offset, properties.v_offset]);
    const safeNear = Math.max(0.001, properties.near);
    const safeFar = Math.max(safeNear + 0.1, properties.far);
    const lens = useMemo(() => ({
        projection: properties.projection,
        fov: properties.fov,
        size: properties.size,
        keepAspect: properties.keep_aspect,
        frustumOffset: properties.frustum_offset,
    }), [
        properties.projection,
        properties.fov,
        properties.size,
        properties.keep_aspect,
        properties.frustum_offset,
    ]);
    const CameraElement = properties.projection === Camera3DProjectionType.ORTHOGONAL ? 'orthographicCamera' : 'perspectiveCamera';
    // PROJECTION_FRUSTUM draws through a perspective camera with a view offset. A new three camera
    // class remounts, through the key, the tag and the lens fit with it.
    return (_jsxs(_Fragment, { children: [_jsx(SceneCamera, { element: CameraElement, name: node.name, tscnPath: tscnPath, position: offsetPosition, rotation: rotation, scale: scale, lens: lens, near: safeNear, far: safeFar, children: children }, CameraElement), _jsx(CameraGizmo, { lens: lens, near: properties.near, position: position, rotation: rotation, scale: scale })] }));
}
function SceneCamera({ element: CameraElement, name, tscnPath, position, rotation, scale, lens, near, far, children, }) {
    const cameraRef = useRef(null);
    useRegisterSceneCamera(tscnPath, cameraRef);
    useEffect(() => {
        if (cameraRef.current) {
            // Tag the camera with its TSCN path so a SubViewport pass can find it.
            cameraRef.current.userData.tscnPath = tscnPath;
        }
    }, [tscnPath]);
    // `manual` keeps R3F's resize from refitting the projection to the canvas in pixels. The viewport
    // that renders through the camera refits it to its own aspect, and a lens change keeps that aspect.
    useLayoutEffect(() => {
        const camera = cameraRef.current;
        if (!camera)
            return;
        camera.manual = true;
        setLens(camera, lens);
        fitToAspect(camera, fittedAspect(camera) ?? DEFAULT_ASPECT);
    }, [lens, near, far]);
    return (_jsx(CameraElement, { ref: cameraRef, name: name, position: position, rotation: rotation, scale: scale, near: near, far: far, children: children }));
}
//# sourceMappingURL=Component.js.map